import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Button from "../components/Button";
import Modal from "../components/Modal";
import ModalCard from "../components/ModalCard";
import ModalSection from "../components/ModalSection";
import ModalActions from "../components/ModalAction";
import FormField from "../components/FormField";
import TextInput from "../components/TextInput";
import nexiConnectSchema from "../shared/schema/nexiConnectSchema";
import GoogleMapsSearch from "../components/GoogleMapsSearch";
import { useMutation, useQuery } from "react-query";
import { toast } from "react-toastify";
import {
  connectNexi,
  connectNexiToCard,
  deleteStripeAccount,
  getNexiAccountList,
  setStripeAccount,
} from "../shared/queries";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import AddCreditCard from "../components/payment/AddCreditCard";
import ConfirmDialog from "../components/common/ConfirmDialog";
import DropdownMenu from "../components/DropdownMenu";

type Props = {};

const NexiConnect = (props: Props) => {
  const { t } = useTranslation();
  const [nexiModal, setNexiModal] = useState(0);
  const [stripePromise, setStripePromise] = useState<any>();
  const [newNexiAccount, setNewNexiAccount] = useState({
    alias: "", // ALIAS_WEB_00090010
    secret_key: "", // U7C0GHLM6VT0A0KP73ZJZV1N00V66XF1
    name: "",
    company: "",
    email: "",
    address: "",
    city: "",
    housenumber: "",
  });
  const [validationError, setValidationError] = useState<any>();
  const [clientSecret, setClientSecret] = useState<string>();
  const [userBillId, setUserBillId] = useState();
  const [showSetAll, setShowSetAll] = useState(false);
  const [currentAccount, setCurrentAccount] = useState<number | undefined>();
  const [showDelete, setShowDelete] = useState(false);

  const { data: nexiAccountList, refetch: refetchNexiAccountList } =
    useQuery<any>(["nexiAccountList"], () => getNexiAccountList());

  const connectNexiMutation = useMutation((body: any) => connectNexi(body), {
    onSuccess: (data) => {
      toast.success(t("general.operationCompleted"));
      setStripePromise(loadStripe(data.results.stripe_p_key));
      setClientSecret(data.results.setup_intent.client_secret);
      setUserBillId(data.results.user_bill_id);
      setNexiModal(2);
    },
    onError: () => {
      toast.error(t("general.requestError"));
    },
  });

  const saveCardMethodToNexi = useMutation(
    (data: any) =>
      connectNexiToCard({
        user_bill_id: data.user_bill_id,
        setupIntent: data.setupIntent,
        type: data.type,
      }),
    {
      onSuccess: () => {
        toast.success(t("general.operationCompleted"));
        setNexiModal(0);
        setNewNexiAccount({
          alias: "",
          secret_key: "",
          name: "",
          company: "",
          email: "",
          address: "",
          city: "",
          housenumber: "",
        });
        refetchNexiAccountList();
      },
      onError: () => {
        toast.error(t("general.requestError"));
        refetchNexiAccountList();
      },
    }
  );

  const setAllMutation = useMutation(
    (id: string | number) =>
      setStripeAccount({
        action: "SETALL",
        stripe_account: id,
      }),
    {
      onSuccess: () => {
        setShowSetAll(false);
        toast.success(t("general.operationCompleted"));
        refetchNexiAccountList();
      },
    }
  );

  const deleteAccountMutation = useMutation(
    (id: string | number) => deleteStripeAccount(id),
    {
      onSuccess: () => {
        setShowDelete(false);
        toast.success(t("general.operationCompleted"));
        refetchNexiAccountList();
      },
    }
  );

  return (
    <div>
      <div className="grid lg:grid-cols-2 lg:p-0 p-1">
        <div className="lg:border-r lg:p-5">
          <img
            className={"h-10"}
            src="/images/logos/nexi-logo.svg"
            alt="Nexi Connect"
          />

          <p className={"text-gray-600 mt-8 md:text-md text-sm"}>
            {t("nexi.ifEnable")}
          </p>
          <p className={"text-gray-600 mt-4 md:text-md text-sm"}>
            {t("nexi.ifNot")}
          </p>
          <p className={"text-gray-600 mt-4 md:text-md text-sm"}>
            {t("nexi.commission")}
          </p>
          <Button
            className={"mt-8"}
            onClick={() => {
              setNexiModal(1);
            }}
            color={"primary"}
            label={t("nexi.activate")}
          />
        </div>

        <div className="lg:p-5 lg:mt-0 mt-8">
          <div className="flex items-center space-x-8 mb-6">
            <div className="font-semibold md:text-xl text-sm">Account Nexi</div>
            <div className="border-b flex-1" />
          </div>
          {nexiAccountList &&
            nexiAccountList.results &&
            nexiAccountList.results.map((account: any) => (
              <div
                className={
                  "flex flex-row items-center lg:space-x-2 justify-between border-b p-1"
                }
                key={account.id}
              >
                <div className="flex space-x-2">
                  <div className={"text-gray-600 md:text-sm text-xs"}>
                    {account.customer_id}
                  </div>

                  <div className={"font-medium md:text-sm text-xs"}>
                    {account.name}
                  </div>
                </div>

                <div className="relative ml-auto text-right overflow-visible">
                  <DropdownMenu
                    className="absolute right-0"
                    triggerLabel=""
                    // color="info"
                    options={[
                      {
                        label: t("stripe.useForAll"),
                        onClick: () => {
                          setCurrentAccount(account.id);
                          setShowSetAll(true);
                        },
                      },
                      {
                        label: t("general.delete"),
                        onClick: () => {
                          setCurrentAccount(account.id);
                          setShowDelete(true);
                        },
                      },
                    ]}
                  />
                </div>
              </div>
            ))}
        </div>
      </div>

      <Modal visible={nexiModal !== 0}>
        <ModalCard title={t("nexi.activate")} className={"md:w-auto w-full"}>
          <ModalSection>
            {nexiModal === 1 && (
              <>
                <div className="grid md:grid-cols-2 px-4 gap-3">
                  {Object.keys(newNexiAccount)
                    .filter(
                      (nexiVal: string) =>
                        nexiVal !== "address" &&
                        nexiVal !== "city" &&
                        nexiVal !== "housenumber"
                    )
                    .map((nexiVal: string) => (
                      <FormField
                        label={t(`nexi.insert${nexiVal}`)}
                        errors={validationError}
                        errorKey={nexiVal}
                      >
                        <TextInput
                          type={"text"}
                          value={
                            newNexiAccount[
                              nexiVal as keyof typeof newNexiAccount
                            ]
                          }
                          onChange={(val) =>
                            setNewNexiAccount((prev) => {
                              return {
                                ...prev,
                                [nexiVal]: val,
                              };
                            })
                          }
                        ></TextInput>
                      </FormField>
                    ))}
                </div>
                <div className="px-4 my-3">
                  <FormField label={t("general.findYourAddressAutoFill")}>
                    <GoogleMapsSearch
                      handleGoogleMaps={(data) => {
                        setNewNexiAccount((prev) => {
                          return {
                            ...prev,
                            city: data.city_en,
                            address: data.address,
                            housenumber: data.housenumber,
                          };
                        });
                      }}
                    />
                  </FormField>
                </div>
                <div className="grid md:grid-cols-2 px-4 gap-3">
                  {Object.keys(newNexiAccount)
                    .filter(
                      (nexiVal: string) =>
                        nexiVal === "address" ||
                        nexiVal === "city" ||
                        nexiVal === "housenumber"
                    )
                    .map((nexiVal: string) => (
                      <FormField
                        label={t(`nexi.insert${nexiVal}`)}
                        errors={validationError}
                        errorKey={nexiVal}
                      >
                        <TextInput
                          type={"text"}
                          className="bg-gray-100"
                          disabled={nexiVal !== "housenumber"}
                          value={
                            newNexiAccount[
                              nexiVal as keyof typeof newNexiAccount
                            ]
                          }
                          onChange={(val) =>
                            setNewNexiAccount((prev) => {
                              return {
                                ...prev,
                                [nexiVal]: val,
                              };
                            })
                          }
                        ></TextInput>
                      </FormField>
                    ))}
                </div>
                <div className="mt-52"></div>
              </>
            )}
            {nexiModal === 2 && (
              <>
                <Elements
                  stripe={stripePromise}
                  key={stripePromise}
                  options={{ clientSecret: clientSecret }}
                >
                  <AddCreditCard
                    visible={nexiModal === 2}
                    onClose={() => {
                      setNexiModal(0);
                    }}
                    onSource={(data: any) => {
                      saveCardMethodToNexi.mutate({
                        user_bill_id: userBillId,
                        setupIntent: data,
                        type: "card",
                      });
                    }}
                    onSave={() => {}}
                  />
                </Elements>
              </>
            )}
          </ModalSection>
          <ModalActions
            isLoading={false}
            saveLabel={t("general.save")}
            onClose={() => {
              setNexiModal(0);
            }}
            onSave={() => {
              if (nexiModal === 1) {
                const { error, value } =
                  nexiConnectSchema.validate(newNexiAccount);
                if (!error) {
                  const obj = {
                    alias: value.alias,
                    secret_key: value.secret_key,
                    name: value.name,
                    company: value.company,
                    email: value.email,
                    address: `${value.address}, ${value.housenumber}`,
                    city: value.city,
                  };
                  connectNexiMutation.mutate(obj);
                  setValidationError(null);
                } else {
                  setValidationError(error);
                }
              }
            }}
          ></ModalActions>
        </ModalCard>
      </Modal>

      <ConfirmDialog
        askConfirmation={true}
        message={
          "Sei sicuro di voler associare questo account Nexi Connect a tutti gli appartamenti?"
        }
        confirmationKey={t("general.iConfirm")}
        visible={showSetAll}
        onCancel={() => {
          setShowSetAll(false);
        }}
        onConfirm={() => {
          if (currentAccount && nexiAccountList)
            setAllMutation.mutate(currentAccount);
        }}
      />

      <ConfirmDialog
        askConfirmation={true}
        message={"Sei sicuro di voler eliminare questo account Nexi Connect?"}
        confirmationKey={t("general.iConfirm")}
        visible={showDelete}
        onCancel={() => {
          setShowDelete(false);
        }}
        onConfirm={() => {
          if (currentAccount && nexiAccountList)
            deleteAccountMutation.mutate(currentAccount);
        }}
      />
    </div>
  );
};

export default NexiConnect;
